import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import CircularProgress from "@mui/material/CircularProgress";
import Autocomplete from "@mui/material/Autocomplete";
import IconButton from "@mui/material/IconButton";
import { useHistory, useParams } from "react-router-dom";
import { ApiLink } from "../../APILink/ApiLink";
import authHeader from "../../Auth/authHeader";
import LoginUserDetailed from "../../Auth/LoginUserDetailed";
import { FiscalYearContext } from "../../FiscalYear/FiscalYearContext";
import { useVatDeclarant } from "../../GetData/Context/CompanyDataContext";
import { getUnitID } from "../../Auth/GetUnitCompany";
import { errorAlert, getApiErrorMessage, handleSuccess } from "../../Components/Alerts";
import AccountsAutocompleteEdit from "../../Components/AccountsAutocompleteEdit";
import AccountsAutocompleteInsert from "../../Components/AccountsAutocompleteInsert";
import PriceField from "../../Components/NumberFieldFormater";
import SuppliersModal from "../SuppliersCRUD/SuppliersModal";
import LongTermAssetsModal from "../LongTermAssetsCRUD/LongTermAssetsModal";
import "./InvestmentPurchaseTotalForm.scss";

const round2 = (value) => {
  const parsed = Number(value || 0);
  if (!Number.isFinite(parsed)) return 0;
  return Math.round((parsed + Number.EPSILON) * 100) / 100;
};

const formatEuroValue = (value) =>
  Number(value || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const TRANSACTION_TYPES = [
  { id: 1, type: "Vendor" },
  { id: 2, type: "Import" },
];

const createEmptySnapshot = () => ({
  invoiceNR: "",
  date: "",
  payDate: "",
  supplierID: "",
  assetID: "",
  paymentOptions: "",
  transactionType: 1,
  coment: "",
  amount0: 0,
  amount8: 0,
  amount18: 0,
  vat8: 0,
  vat18: 0,
  valueWithVAT0: 0,
  valueWithVAT8: 0,
  valueWithVAT18: 0,
});

export default function InvestmentPurchaseTotalForm() {
  const history = useHistory();
  const { transactionId } = useParams();
  const headers = useMemo(() => authHeader(), []);
  const createdByUser = LoginUserDetailed();
  const { fiscalYear } = useContext(FiscalYearContext);
  const isVatDeclarant = useVatDeclarant();
  const isEditMode = Boolean(transactionId);

  const [minDate, setMinDate] = useState("");
  const [maxDate, setMaxDate] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isInitializing, setIsInitializing] = useState(isEditMode);
  const [editingTransactionId, setEditingTransactionId] = useState("");

  const [invoiceNR, setInvoiceNR] = useState("");
  const [date, setDate] = useState("");
  const [payDate, setPayDate] = useState("");
  const [supplierID, setSupplierID] = useState("");
  const [supplierOption, setSupplierOption] = useState(null);
  const [assetID, setAssetID] = useState("");
  const [assetOption, setAssetOption] = useState(null);
  const [paymentOptions, setPaymentOptions] = useState("");
  const [transactionType, setTransactionType] = useState(1);
  const [coment, setComent] = useState("");
  const [unitId, setUnitId] = useState(null);

  const [amount0, setAmount0] = useState(0);
  const [amount8, setAmount8] = useState(0);
  const [amount18, setAmount18] = useState(0);
  const [vat8, setVat8] = useState(0);
  const [vat18, setVat18] = useState(0);
  const [valueWithVAT0, setValueWithVAT0] = useState(0);
  const [valueWithVAT8, setValueWithVAT8] = useState(0);
  const [valueWithVAT18, setValueWithVAT18] = useState(0);

  const [cashRegister, setCashRegister] = useState([]);
  const [longTermAssets, setLongTermAssets] = useState([]);

  const [invNumberExist, setInvNumberExist] = useState(0);
  const [invoiceOtherSupplierMessage, setInvoiceOtherSupplierMessage] = useState("");

  const [showSupplierModal, setShowSupplierModal] = useState(false);
  const [showLongTermAssetsModal, setShowLongTermAssetsModal] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [initialFormSnapshot, setInitialFormSnapshot] = useState(createEmptySnapshot());
  const invoiceNrInputRef = useRef(null);

  const [touchedFields, setTouchedFields] = useState({
    date: false,
    payDate: false,
    paymentOptions: false,
  });

  const [validationErrors, setValidationErrors] = useState({
    invoiceNR: "",
    date: "",
    payDate: "",
    supplierID: "",
    assetID: "",
    paymentOptions: "",
  });

  useEffect(() => {
    setMinDate(`${fiscalYear}-01-01`);
    setMaxDate(`${fiscalYear}-12-31`);
  }, [fiscalYear]);

  useEffect(() => {
    getUnitID(headers).then((id) => setUnitId(id || null));
  }, [headers]);

  const fetchPaymentType = async () => {
    try {
      const response = await axios.get(ApiLink.API_URL + "Accounts/paymenttype", { headers });
      setCashRegister(response.data || []);
    } catch (error) {
      errorAlert(getApiErrorMessage(error, "Gabim ne marrjen e metyrave te pageses."));
    }
  };

  const fetchLongTermAssets = async () => {
    try {
      const response = await axios.get(ApiLink.API_URL + "Accounts/LongTermAssets", { headers });
      setLongTermAssets(response.data || []);
    } catch (error) {
      errorAlert(getApiErrorMessage(error, "Gabim ne marrjen e aseteve afatgjata."));
    }
  };

  useEffect(() => {
    fetchPaymentType();
    fetchLongTermAssets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isEditMode) setInitialFormSnapshot(createEmptySnapshot());
  }, [isEditMode]);

  useEffect(() => {
    if (isInitializing || isEditMode) {
      return;
    }

    const timerId = window.setTimeout(() => {
      if (invoiceNrInputRef.current) {
        invoiceNrInputRef.current.focus();
      }
    }, 0);

    return () => window.clearTimeout(timerId);
  }, [isInitializing, isEditMode]);

  useEffect(() => {
    if (!assetID || longTermAssets.length === 0) return;
    const found = longTermAssets.find((a) => String(a.AccountID) === String(assetID));
    if (found && (!assetOption || String(assetOption.AccountID) !== String(assetID))) {
      setAssetOption(found);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [longTermAssets, assetID]);

  useEffect(() => {
    if (!invoiceNR || !supplierID) {
      setInvNumberExist(0);
      setInvoiceOtherSupplierMessage("");
      return;
    }

    let isCancelled = false;

    const validateInvoice = async () => {
      try {
        const response = await axios.get(
          ApiLink.API_URL + "InvestmentPurchases/invoice-validation",
          {
            headers,
            params: {
              invNumber: invoiceNR,
              fiscalYear,
              supplierId: Number(supplierID),
              transactionId: isEditMode
                ? Number(editingTransactionId || transactionId || 0)
                : 0,
            },
          },
        );

        if (isCancelled) return;

        const sameSupplierExists = Boolean(response?.data?.sameSupplierExists);
        const existsWithOtherSupplier = Boolean(response?.data?.existsWithOtherSupplier);
        const otherSupplierNames = response?.data?.otherSupplierNames || "";

        setInvNumberExist(sameSupplierExists ? 1 : 0);

        if (!sameSupplierExists && existsWithOtherSupplier) {
          setInvoiceOtherSupplierMessage(
            `Ky numer iatures ekziston per furnitor tjeter: ${otherSupplierNames}`,
          );
        } else {
          setInvoiceOtherSupplierMessage("");
        }
      } catch {
        if (!isCancelled) {
          setInvNumberExist(0);
          setInvoiceOtherSupplierMessage("");
        }
      }
    };

    validateInvoice();
    return () => { isCancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceNR, supplierID, fiscalYear, isEditMode, editingTransactionId, transactionId]);

  const recalcByNet = (net, vatRate) => {
    const base = Number(net || 0);
    const vat = base * (vatRate / 100);
    const total = base + vat;
    if (vatRate === 18) { setAmount18(base); setVat18(vat); setValueWithVAT18(total); }
    if (vatRate === 8)  { setAmount8(base);  setVat8(vat);  setValueWithVAT8(total);  }
    if (vatRate === 0)  { setAmount0(base);  setValueWithVAT0(base); }
  };

  const recalcByTotal = (gross, vatRate) => {
    const total = Number(gross || 0);
    const base = vatRate === 0 ? total : (total * 100) / (100 + vatRate);
    const vat = total - base;
    if (vatRate === 18) { setAmount18(base); setVat18(vat); setValueWithVAT18(total); }
    if (vatRate === 8)  { setAmount8(base);  setVat8(vat);  setValueWithVAT8(total);  }
    if (vatRate === 0)  { setAmount0(base);  setValueWithVAT0(total); }
  };

  const recalcFromVAT = (vatValue, vatRate) => {
    if (vatRate === 0) return;
    const vat = Number(vatValue || 0);
    const base = vat / (vatRate / 100);
    const total = base + vat;
    if (vatRate === 18) { setAmount18(base); setVat18(vat); setValueWithVAT18(total); }
    if (vatRate === 8)  { setAmount8(base);  setVat8(vat);  setValueWithVAT8(total);  }
  };

  const totals = useMemo(() => {
    const totalNoVat = round2(Number(amount0) + Number(amount8) + Number(amount18));
    const totalVat = round2(Number(vat8) + Number(vat18));
    const totalWithVat = round2(totalNoVat + totalVat);
    return { totalNoVat, totalVat, totalWithVat };
  }, [amount0, amount8, amount18, vat8, vat18]);

  const currentFormSnapshot = useMemo(
    () => ({
      invoiceNR: invoiceNR || "",
      date: date || "",
      payDate: payDate || "",
      supplierID: String(supplierID || ""),
      assetID: String(assetID || ""),
      paymentOptions: String(paymentOptions || ""),
      transactionType: Number(transactionType || 1),
      coment: coment || "",
      amount0: round2(amount0),
      amount8: round2(amount8),
      amount18: round2(amount18),
      vat8: round2(vat8),
      vat18: round2(vat18),
      valueWithVAT0: round2(valueWithVAT0),
      valueWithVAT8: round2(valueWithVAT8),
      valueWithVAT18: round2(valueWithVAT18),
    }),
    [invoiceNR, date, payDate, supplierID, assetID, paymentOptions, transactionType, coment,
     amount0, amount8, amount18, vat8, vat18, valueWithVAT0, valueWithVAT8, valueWithVAT18],
  );

  const isFormDirty = useMemo(
    () => JSON.stringify(currentFormSnapshot) !== JSON.stringify(initialFormSnapshot),
    [currentFormSnapshot, initialFormSnapshot],
  );

  const isInsertFormEmpty = useMemo(() => {
    if (isEditMode) return false;
    return (
      !invoiceNR &&
      !date &&
      !payDate &&
      !supplierID &&
      !assetID &&
      !paymentOptions &&
      !coment &&
      amount0 === 0 &&
      amount8 === 0 &&
      amount18 === 0
    );
  }, [
    isEditMode,
    invoiceNR,
    date,
    payDate,
    supplierID,
    assetID,
    paymentOptions,
    coment,
    amount0,
    amount8,
    amount18,
  ]);

  const closePage = () => history.push("/purchases/investmentpurchases");

  const handleCloseClick = () => {
    if (isFormDirty) { setShowCloseConfirm(true); return; }
    closePage();
  };

  const resetForm = () => {
    setInvoiceNR(""); setDate(""); setPayDate("");
    setSupplierID(""); setSupplierOption(null);
    setAssetID(""); setAssetOption(null);
    setPaymentOptions(""); setTransactionType(1); setComent("");
    setAmount0(0); setAmount8(0); setAmount18(0);
    setVat8(0); setVat18(0);
    setValueWithVAT0(0); setValueWithVAT8(0); setValueWithVAT18(0);
    setInvNumberExist(0); setInvoiceOtherSupplierMessage("");
    setInitialFormSnapshot(createEmptySnapshot());
    setTouchedFields({ date: false, payDate: false, paymentOptions: false });
  };

  const fillFormForEdit = (item) => {
    const safeInvoiceNr = item?.InvoiceNr || "";
    const invoiceDateIso = (item?.Date || "").substring(0, 10);
    const paymentDateIso = (item?.PayDate || item?.Date || "").substring(0, 10);
    const supplierAccountId = item?.AccountID || "";
    const assetAccountId = item?.AccountIDSpare || "";
    const a0 = Number(item?.Amount0 || 0);
    const a8 = Number(item?.Amount8 || 0);
    const a18 = Number(item?.Amount18 || 0);
    const v8 = Number(item?.VAT8 || 0);
    const v18 = Number(item?.VAT18 || 0);
    const vat0 = a0;
    const tot8 = a8 + v8;
    const tot18 = a18 + v18;

    setEditingTransactionId(String(item?.TransactionID || transactionId || ""));
    setInvoiceNR(safeInvoiceNr);
    setDate(invoiceDateIso);
    setPayDate(paymentDateIso);
    setSupplierID(String(supplierAccountId));
    setSupplierOption(
      supplierAccountId
        ? { AccountID: supplierAccountId, AccountName: item?.AccountName || "" }
        : null,
    );
    setAssetID(String(assetAccountId));
    setAssetOption(null);
    setPaymentOptions(String(item?.PaymentOptions ?? ""));
    setTransactionType(Number(item?.TransactionType || 1));
    setComent(item?.Coment || "");
    setAmount0(a0); setAmount8(a8); setAmount18(a18);
    setVat8(v8); setVat18(v18);
    setValueWithVAT0(vat0); setValueWithVAT8(tot8); setValueWithVAT18(tot18);

    setInitialFormSnapshot({
      invoiceNR: safeInvoiceNr,
      date: invoiceDateIso,
      payDate: paymentDateIso,
      supplierID: String(supplierAccountId),
      assetID: String(assetAccountId),
      paymentOptions: String(item?.PaymentOptions ?? ""),
      transactionType: Number(item?.TransactionType || 1),
      coment: item?.Coment || "",
      amount0: round2(a0), amount8: round2(a8), amount18: round2(a18),
      vat8: round2(v8), vat18: round2(v18),
      valueWithVAT0: round2(vat0), valueWithVAT8: round2(tot8), valueWithVAT18: round2(tot18),
    });
  };

  useEffect(() => {
    let isCancelled = false;
    const initEditMode = async () => {
      if (!isEditMode) { setIsInitializing(false); return; }
      try {
        const response = await axios.get(
          ApiLink.API_URL + `InvestmentPurchases/view-mode/${transactionId}`,
          { headers },
        );
        if (!isCancelled) fillFormForEdit(response.data);
      } catch {
        if (!isCancelled) {
          errorAlert("Gabim ne marrjen e te dhenave per editim.");
          history.push("/purchases/investmentpurchases");
        }
      } finally {
        if (!isCancelled) setIsInitializing(false);
      }
    };
    initEditMode();
    return () => { isCancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, transactionId]);

  const validateBeforeSubmit = () => {
    const nextErrors = {
      invoiceNR: "",
      date: "",
      payDate: "",
      supplierID: "",
      assetID: "",
      paymentOptions: "",
    };
    if (!invoiceNR)      nextErrors.invoiceNR = "Numri i faturës është i detyrueshëm.";
    if (!date)           nextErrors.date = "Data e faturës është e detyrueshme.";
    if (!payDate)        nextErrors.payDate = "Data e pagesës është e detyrueshme.";
    if (!supplierID)     nextErrors.supplierID = "Ju lutem zgjidhni furnitorin.";
    if (!assetID)        nextErrors.assetID = "Ju lutem zgjidhni asetin.";
    if (!paymentOptions) nextErrors.paymentOptions = "Ju lutem zgjidhni mënyrën e pagesës.";
    if (Object.values(nextErrors).some(Boolean)) {
      setValidationErrors(nextErrors);
      return false;
    }
    if (invNumberExist > 0) { errorAlert("Ky numër i faturës ekziston në listë."); return false; }
    setValidationErrors({ invoiceNR: "", date: "", payDate: "", supplierID: "", assetID: "", paymentOptions: "" });
    return true;
  };

  const focusInvoiceField = () => {
    window.setTimeout(() => {
      if (invoiceNrInputRef.current) {
        invoiceNrInputRef.current.focus();
        invoiceNrInputRef.current.select?.();
      }
    }, 0);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateBeforeSubmit()) {
      if (!isEditMode) {
        focusInvoiceField();
      }
      return;
    }
    setIsSubmitting(true);
    try {
      if (isEditMode) {
        const editPayload = {
          TransactionID: Number(editingTransactionId || transactionId),
          Date: date, PayDate: payDate, ActionType: "INP", InvoiceNr: invoiceNR,
          AccountID: supplierID, AccountIDSpare: assetID,
          Amount0: amount0, Amount8: amount8, Amount18: amount18,
          PaymentOptions: Number(paymentOptions || 0),
          TransactionType: Number(transactionType),
          CompanyUnitID: unitId && Number(unitId) > 0 ? Number(unitId) : null,
          Coment: coment, ModifiedByUser: createdByUser,
        };
        await axios.put(ApiLink.API_URL + "InvestmentPurchases/update-investment-purchase", editPayload, { headers });
        handleSuccess("Blerja investive u perditesua me sukses");
        closePage();
      } else {
        const insertPayload = {
          Date: date, PayDate: payDate, ActionType: "INP", InvoiceNr: invoiceNR,
          AccountID: assetID, SupplierID: supplierID,
          Amount0: amount0, Amount8: amount8, Amount18: amount18,
          PaymentOptions: Number(paymentOptions || 0),
          TransactionType: Number(transactionType),
          CompanyUnitID: unitId && Number(unitId) > 0 ? Number(unitId) : null,
          Coment: coment, CreatedByUser: createdByUser,
        };
        await axios.post(ApiLink.API_URL + "InvestmentPurchases/insert-investment-purchase", insertPayload, { headers });
        handleSuccess("Blerja investive u ruajt me sukses");
        resetForm();
        focusInvoiceField();
      }
    } catch (error) {
      if (error?.response?.status === 409) {
        errorAlert(error.response.data);
      } else {
        errorAlert(getApiErrorMessage(error, isEditMode ? "Ndodhi gabim gjate perditesimit." : "Ndodhi gabim gjate ruajtjes."));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const sharedDropdownIcon = (props) => (
    <svg {...props} width="22" height="22" viewBox="0 0 24 24" fill="none"
      stroke="var(--cc-chart-bar-credit)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );

  const paymentMenuProps = {
    PaperProps: {
      className: "iptf-payment-menu-paper",
      sx: {
        maxHeight: 280, maxWidth: "min(460px, calc(100vw - 32px))",
        mt: 0.5, overflowY: "auto", borderRadius: "10px",
        background: "var(--app-surface)", color: "var(--app-text)",
        border: "1px solid var(--dg-border)",
        "& .MuiMenuItem-root": {
          fontSize: "13px", minHeight: 36, whiteSpace: "normal", wordBreak: "break-word",
          color: "var(--app-text)",
          "&:hover": { background: "var(--dg-row-hover)" },
        },
      },
    },
  };

  const sharedInputSx = {
    borderRadius: 8, height: 48, minHeight: 48, maxHeight: 48,
    background: "var(--dg-input-bg)", color: "var(--dg-input-text)",
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "var(--cc-chart-bar-credit)" },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "var(--cc-chart-bar-credit)" },
    "& .MuiSelect-select, & .MuiInputBase-input": {
      height: 48, minHeight: 48, maxHeight: 48,
      boxSizing: "border-box", display: "flex", alignItems: "center", padding: "0 14px",
    },
  };

  if (isInitializing) {
    return (
      <div className="iptf-form-page" style={{ minHeight: "280px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", minHeight: "280px" }}>
          <CircularProgress size={28} />
        </div>
      </div>
    );
  }

  return (
    <div className="iptf-form-page">
      <div className="iptf-form-header">
        <div>
          <h2 className="iptf-form-title">
            {isEditMode ? "Ndrysho blerjen investive" : "Blerje Investive e re"}
          </h2>
          <p className="iptf-form-subtitle">
            {isEditMode
              ? "Perditeso fushat e blerjes investive dhe ruaj ndryshimet."
              : "Ploteso fushat kryesore dhe ruaj faturen e blerjes investive."}
          </p>
        </div>
        <div className="iptf-form-header-actions">
          {!isEditMode && (
            <button type="button" className="iptf-btn iptf-btn-secondary" onClick={resetForm} disabled={isSubmitting}>
              Pastro
            </button>
          )}
          <button type="button" className="iptf-btn iptf-btn-ghost" onClick={handleCloseClick} disabled={isSubmitting}>
            Mbyll
          </button>
          <button
            type="submit" form="inp-form"
            className={"iptf-btn iptf-btn-primary" + (invNumberExist > 0 ? " invoice-exists" : "")}
            disabled={
              isSubmitting ||
              invNumberExist > 0 ||
              (!isEditMode && isInsertFormEmpty) ||
              (isEditMode && !isFormDirty)
            }
          >
            {isSubmitting ? (
              <CircularProgress size={18} style={{ color: "#fff" }} />
            ) : invNumberExist > 0 ? (
              "Ky numer iatures ekziston"
            ) : isEditMode ? (
              "Perditeso"
            ) : (
              "Ruaj"
            )}
          </button>
        </div>
      </div>

      <form id="inp-form" className="iptf-form-body" onSubmit={handleSubmit} noValidate>
        <div className="iptf-top-grid">
          <section className="iptf-card iptf-card-elevated">
            <div className="iptf-card-headline">
              <h3 className="iptf-card-title">Informacioni i Pergjithshem</h3>
              <span className="iptf-card-chip">Dokumenti</span>
            </div>
            <div className="iptf-form-cluster">
              <p className="iptf-cluster-title">Detajet e Dokumentit</p>
              <div className="iptf-grid two-columns">
                <div className="iptf-grid-full">
                  <TextField
                    label="Numri Fatures" fullWidth required
                    inputRef={invoiceNrInputRef}
                    value={invoiceNR}
                    onChange={(e) => {
                      setInvoiceNR(e.target.value);
                      if (validationErrors.invoiceNR) setValidationErrors((prev) => ({ ...prev, invoiceNR: "" }));
                    }}
                    error={invNumberExist > 0 || Boolean(validationErrors.invoiceNR)}
                    helperText={invNumberExist > 0 ? "Ky numër i faturës ekziston në listë." : validationErrors.invoiceNR || undefined}
                    InputLabelProps={{ shrink: true }}
                  />
                </div>

                <TextField
                  label="Data Fatures" type="date" value={date}
                  onChange={(e) => {
                    setDate(e.target.value); setPayDate(e.target.value);
                    if (validationErrors.date) setValidationErrors((prev) => ({ ...prev, date: "" }));
                    if (validationErrors.payDate) setValidationErrors((prev) => ({ ...prev, payDate: "" }));
                  }}
                  required onBlur={() => setTouchedFields((p) => ({ ...p, date: true }))}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: minDate, max: maxDate }}
                  error={(touchedFields.date && !date) || Boolean(validationErrors.date)}
                  helperText={(touchedFields.date && !date) ? "Data e faturës është e detyrueshme." : validationErrors.date || ""}
                />

                <TextField
                  label="Data pageses" type="date" value={payDate}
                  onChange={(e) => {
                    setPayDate(e.target.value);
                    if (validationErrors.payDate) setValidationErrors((prev) => ({ ...prev, payDate: "" }));
                  }}
                  required onBlur={() => setTouchedFields((p) => ({ ...p, payDate: true }))}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: minDate, max: maxDate }}
                  error={(touchedFields.payDate && !payDate) || Boolean(validationErrors.payDate)}
                  helperText={(touchedFields.payDate && !payDate) ? "Data e pagesës është e detyrueshme." : validationErrors.payDate || ""}
                />

                <div className="iptf-grid-full">
                  <div style={{ display: "flex", alignItems: "center", gap: 0 }}>
                    <div style={{ flex: 1 }}>
                      {isEditMode ? (
                        <AccountsAutocompleteEdit
                          ledgerID={302} textFieldWidth="100%"
                          accountID={supplierID} label="Kerko Furnitorin" labelId="SupplierID"
                          onSelect={(account) => {
                            setSupplierID(account.AccountID); setSupplierOption(account);
                            if (validationErrors.supplierID) setValidationErrors((prev) => ({ ...prev, supplierID: "" }));
                          }}
                          onClear={() => { setSupplierID(""); setSupplierOption(null); }}
                        />
                      ) : (
                        <AccountsAutocompleteInsert
                          ledgerID={302} textFieldWidth="100%"
                          value={supplierOption} label="Kerko Furnitorin" labelId="SupplierID"
                          disableAutoFocus
                          onSelect={(account) => {
                            setSupplierID(account.AccountID); setSupplierOption(account);
                            if (validationErrors.supplierID) setValidationErrors((prev) => ({ ...prev, supplierID: "" }));
                          }}
                          onClear={() => { setSupplierID(""); setSupplierOption(null); }}
                        />
                      )}
                    </div>
                    <IconButton size="small" onClick={() => setShowSupplierModal(true)}
                      aria-label="Shto furnitor" title="Shto furnitor"
                      sx={{
                        border: "1px solid var(--header-control-border)", borderRadius: "8px", color: "var(--app-text)",
                        background: "var(--header-control-bg)",
                        fontSize: "22px", fontWeight: 700,
                        width: 48, height: 48, minWidth: 48, minHeight: 48, maxWidth: 48, maxHeight: 48,
                        p: 0, lineHeight: 1, display: "flex", alignItems: "center", justifyContent: "center",
                      }}
                    >+</IconButton>
                  </div>
                  {validationErrors.supplierID && (
                    <div style={{ marginTop: 6, color: "#d32f2f", fontSize: 12, lineHeight: 1.3 }}>
                      {validationErrors.supplierID}
                    </div>
                  )}
                </div>

                {invoiceOtherSupplierMessage && invNumberExist <= 0 && (
                  <div className="iptf-grid-full">
                    <div style={{
                      marginTop: 4, padding: "10px 12px", borderRadius: 8,
                      border: "1px solid var(--cc-card-border)", background: "var(--cc-card-bg-soft)",
                      color: "var(--app-text)", fontSize: 12, lineHeight: 1.35,
                    }}>
                      {invoiceOtherSupplierMessage}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </section>

          <section className="iptf-card iptf-card-elevated">
            <div className="iptf-card-headline">
              <h3 className="iptf-card-title">Klasifikimi</h3>
              <span className="iptf-card-chip">Kontabiliteti</span>
            </div>
            <div className="iptf-form-cluster">
              <p className="iptf-cluster-title">Kategorizimi i Blerjes Investive</p>
              <div className="iptf-grid two-columns">

                <div className="iptf-grid-full">
                  <div style={{ display: "flex", gap: 0, alignItems: "center" }}>
                    <div style={{ flex: 1 }}>
                      <Autocomplete
                        options={longTermAssets} value={assetOption}
                        componentsProps={{
                          paper: {
                            sx: {
                              background: "var(--app-surface)", color: "var(--app-text)",
                              border: "1px solid var(--dg-border)",
                              "& .MuiAutocomplete-option.Mui-focused": { background: "var(--dg-row-hover)" },
                            },
                          },
                        }}
                        onChange={(_, value) => {
                          if (!value) { setAssetID(""); setAssetOption(null); return; }
                          setAssetID(value.AccountID); setAssetOption(value);
                          if (validationErrors.assetID) setValidationErrors((prev) => ({ ...prev, assetID: "" }));
                        }}
                        getOptionLabel={(option) => option.AccountName || ""}
                        isOptionEqualToValue={(option, value) => String(option.AccountID) === String(value.AccountID)}
                        popupIcon={sharedDropdownIcon({})}
                        ListboxProps={{ style: { fontSize: "13px" } }}
                        renderOption={(props, option) => (
                          <li {...props} style={{ display: "flex", alignItems: "center", height: "auto", padding: "8px 16px" }}>
                            {option.AccountName}
                          </li>
                        )}
                        renderInput={(params) => (
                          <TextField {...params} label="Aseti" fullWidth size="small" InputLabelProps={{ shrink: true }}
                            sx={{ 
                              "& .MuiOutlinedInput-root": { padding: "0 !important" }
                            }}
                            InputProps={{
                              ...params.InputProps,
                              sx: {
                                ...sharedInputSx,
                                height: "40px",
                                padding: "0 !important",
                                "& .MuiInputBase-input": { height: "40px", display: "flex", alignItems: "center", padding: "0 14px !important", margin: "0 !important", lineHeight: "40px" },
                                "& .MuiAutocomplete-input": { paddingTop: "0 !important", paddingBottom: "0 !important", marginTop: "0 !important", lineHeight: "40px" },
                              },
                            }}
                          />
                        )}
                      />
                    </div>
                    <IconButton size="small" onClick={() => setShowLongTermAssetsModal(true)}
                      aria-label="Shto aset" title="Shto aset"
                      sx={{
                        border: "1px solid var(--header-control-border)", borderRadius: "8px", color: "var(--app-text)",
                        background: "var(--header-control-bg)",
                        fontSize: "22px", fontWeight: 700,
                        width: 48, height: 48, minWidth: 48, minHeight: 48, maxWidth: 48, maxHeight: 48,
                        p: 0, lineHeight: 1, display: "flex", alignItems: "center", justifyContent: "center",
                      }}
                    >+</IconButton>
                  </div>
                  {validationErrors.assetID && (
                    <div style={{ marginTop: 6, color: "#d32f2f", fontSize: 12, lineHeight: 1.3 }}>
                      {validationErrors.assetID}
                    </div>
                  )}
                </div>

                <TextField select label="Lloji Blerjes" value={transactionType}
                  onChange={(e) => setTransactionType(e.target.value)} required
                  InputLabelProps={{ shrink: true }}
                  SelectProps={{ MenuProps: paymentMenuProps, IconComponent: sharedDropdownIcon }}
                  InputProps={{ sx: sharedInputSx }}
                >
                  {TRANSACTION_TYPES.map((item) => (
                    <MenuItem key={item.id} value={item.id}>{item.type}</MenuItem>
                  ))}
                </TextField>

                <TextField select label="Menyra Pageses" value={paymentOptions}
                  onChange={(e) => {
                    setPaymentOptions(e.target.value);
                    setTouchedFields((p) => ({ ...p, paymentOptions: true }));
                    if (validationErrors.paymentOptions) setValidationErrors((prev) => ({ ...prev, paymentOptions: "" }));
                  }}
                  onBlur={() => setTouchedFields((p) => ({ ...p, paymentOptions: true }))}
                  error={(touchedFields.paymentOptions && !paymentOptions) || Boolean(validationErrors.paymentOptions)}
                  helperText={(touchedFields.paymentOptions && !paymentOptions) ? "Ju lutem zgjidhni mënyrën e pagesës." : validationErrors.paymentOptions || ""}
                  SelectProps={{ MenuProps: paymentMenuProps, IconComponent: sharedDropdownIcon }}
                  InputProps={{ sx: sharedInputSx }}
                  InputLabelProps={{ shrink: true }}
                >
                  <MenuItem key="0" value="0">Pagesa Behet me vone</MenuItem>
                  {cashRegister.map((data) => (
                    <MenuItem key={data.AccountID} value={String(data.AccountID)}>{data.AccountName}</MenuItem>
                  ))}
                </TextField>

                <div className="iptf-grid-full">
                  <TextField className="iptf-comment" label="Komenti" multiline rows={1} fullWidth
                    value={coment} onChange={(e) => setComent(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </div>
              </div>
            </div>
          </section>
        </div>

        <section className="iptf-card">
          <h3 className="iptf-card-title">Vlerat dhe TVSH</h3>
          <div className="iptf-vat-table">
            <div className="iptf-vat-head">
              <div>Norma TVSH</div>
              <div>Vlera pa TVSH</div>
              <div>Vlera e TVSH</div>
              <div>Total</div>
            </div>
            {isVatDeclarant && (
              <>
                <div className="iptf-vat-row">
                  <div className="iptf-vat-rate">18%</div>
                  <PriceField label="" name="amount18" value={amount18} onChange={(e) => recalcByNet(e.target.value, 18)} />
                  <PriceField label="" name="vat18" value={vat18} onChange={(e) => recalcFromVAT(e.target.value, 18)} />
                  <PriceField label="" name="total18" value={valueWithVAT18} decimalScale={2} onChange={(e) => recalcByTotal(e.target.value, 18)} />
                </div>
                <div className="iptf-vat-row">
                  <div className="iptf-vat-rate">8%</div>
                  <PriceField label="" name="amount8" value={amount8} onChange={(e) => recalcByNet(e.target.value, 8)} />
                  <PriceField label="" name="vat8" value={vat8} onChange={(e) => recalcFromVAT(e.target.value, 8)} />
                  <PriceField label="" name="total8" value={valueWithVAT8} decimalScale={2} onChange={(e) => recalcByTotal(e.target.value, 8)} />
                </div>
              </>
            )}
            <div className="iptf-vat-row">
              <div className="iptf-vat-rate">0%</div>
              <PriceField label="" name="amount0" value={amount0} onChange={(e) => recalcByNet(e.target.value, 0)} />
              <PriceField label="" name="vat0" value={0} disabled />
              <PriceField label="" name="total0" value={valueWithVAT0} decimalScale={2} onChange={(e) => recalcByTotal(e.target.value, 0)} />
            </div>
          </div>

          <div className="iptf-vat-totals-row">
            <div className="iptf-vat-totals-label">Totale</div>
            <div className="iptf-vat-total-item">
              <span>Total pa TVSH</span>
              <strong>{formatEuroValue(totals.totalNoVat)} EUR</strong>
            </div>
            <div className="iptf-vat-total-item">
              <span>Total TVSH</span>
              <strong>{formatEuroValue(totals.totalVat)} EUR</strong>
            </div>
            <div className="iptf-vat-total-item grand-total">
              <span>Total me TVSH</span>
              <strong>{formatEuroValue(totals.totalWithVat)} EUR</strong>
            </div>
          </div>
        </section>
      </form>

      <SuppliersModal show={showSupplierModal} onHide={() => setShowSupplierModal(false)} mode="insert" type="none" />
      <LongTermAssetsModal show={showLongTermAssetsModal} onHide={() => setShowLongTermAssetsModal(false)} mode="insert" type="none" fetchData={fetchLongTermAssets} />

      <Dialog open={showCloseConfirm} onClose={() => setShowCloseConfirm(false)} maxWidth="xs" fullWidth
        PaperProps={{ className: "iptf-close-confirm-dialog" }}>
        <DialogTitle>Konfirmo mbylljen</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Ke ndryshime te paruajtura. Nese mbyll kete forme, te dhenat e pa-ruajtura do te humbin. A je i sigurt qe deshiron te vazhdosh?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setShowCloseConfirm(false)} color="inherit">Anulo</Button>
          <Button
            onClick={() => { setShowCloseConfirm(false); closePage(); }}
            variant="contained"
            sx={{
              background: "var(--dg-header-gradient)", color: "var(--dg-header-text)",
              "&:hover": { background: "var(--header-control-bg-hover)", color: "var(--app-text)" },
            }}
          >
            Po, mbylle
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
